import { useContext, useState, useEffect, useRef } from "react";
import { AuthContext } from "../../utils/AuthContext";
import Header from "../../../components/Header";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { User, Mail, Phone, CheckCircle2, Camera, AlertCircle } from "lucide-react";

const G       = "#3A9D23";
const G_DARK  = "#2f7d1c";
const G_LIGHT = "#f0fdf4";
const API     = "https://api.komorotel.com";

function getInitials(name, email) {
  if (name?.trim()) {
    const parts = name.trim().split(" ");
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  return email?.slice(0, 2).toUpperCase() || "?";
}

/* ── Champ stylisé ── */
function Field({ icon, label, name, type = "text", value, onChange, placeholder, readOnly }) {
  const [focused, setFocused] = useState(false);

  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: 8 }}>
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <div style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: focused ? G : "#9ca3af", transition: "color 0.18s" }}>
          {icon}
        </div>
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          readOnly={readOnly}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%", boxSizing: "border-box",
            paddingLeft: 44, paddingRight: 16, paddingTop: 13, paddingBottom: 13,
            border: `1.5px solid ${focused ? G : "#e5e7eb"}`,
            borderRadius: 12, fontSize: 14, color: readOnly ? "#9ca3af" : "#111827",
            fontFamily: "'DM Sans', system-ui, sans-serif",
            outline: "none", background: readOnly ? "#f9fafb" : "#fff",
            boxShadow: focused ? `0 0 0 3px ${G}18` : "none",
            transition: "border-color 0.18s, box-shadow 0.18s",
            cursor: readOnly ? "not-allowed" : "text",
          }}
        />
      </div>
      {readOnly && (
        <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, marginLeft: 2 }}>Non modifiable</p>
      )}
    </div>
  );
}

export default function Profile() {
  const { user, dispatch } = useContext(AuthContext);

  const [form,    setForm]    = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState("idle"); // idle | success | error
  const [dirty,   setDirty]   = useState(false);

  useEffect(() => {
    if (user) {
      setForm({ name: user.name || "", email: user.email || "", phone: user.phone || "" });
      setDirty(false);
    }
  }, [user]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setDirty(true);
    setStatus("idle");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setStatus("idle");
      const res = await axios.put(`${API}/api/users/${user._id}`, form, { withCredentials: true });
      dispatch({ type: "LOGIN_SUCCESS", payload: res.data });
      setStatus("success");
      setDirty(false);
      setTimeout(() => setStatus("idle"), 4000);
    } catch (err) {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return (
    <>
      <Header />
      <div style={{ minHeight: "100vh", background: "#fafaf9", display: "flex", alignItems: "center", justifyContent: "center", paddingTop: 88 }}>
        <p style={{ fontSize: 15, color: "#9ca3af" }}>Vous n'êtes pas connecté.</p>
      </div>
    </>
  );

  const initials   = getInitials(form.name, form.email);
  const memberSince = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    : null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Playfair+Display:wght@600;700&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin   { to   { transform: rotate(360deg); } }
      `}</style>

      <Header />

      <div style={{
        minHeight: "100vh", background: "#fafaf9",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        paddingTop: 96, paddingBottom: 80,
      }}>
        <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 24px" }}>

          {/* ── En-tête page ── */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} style={{ marginBottom: 32 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
              <div style={{ width: 4, height: 28, borderRadius: 99, background: G }} />
              <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(22px,5vw,28px)", fontWeight: 700, color: "#111827", margin: 0 }}>
                Mon profil
              </h1>
            </div>
            <p style={{ fontSize: 13, color: "#9ca3af", marginLeft: 16, fontStyle: "italic" }}>
              Gérez vos informations personnelles
            </p>
          </motion.div>

          {/* ── Card avatar ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08, duration: 0.4 }}
            style={{ background: "#fff", borderRadius: 20, border: "1px solid #efefef", padding: "28px 28px 24px", marginBottom: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>

            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>

              {/* Avatar */}
              <div style={{ position: "relative", flexShrink: 0 }}>
                <div style={{
                  width: 72, height: 72, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${G}, ${G_DARK})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 22, fontWeight: 800, color: "#fff",
                  letterSpacing: "0.03em",
                  boxShadow: `0 4px 20px ${G}40`,
                }}>
                  {user.avatar
                    ? <img src={user.avatar} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
                    : initials}
                </div>
                {/* Pastille verte online */}
                <div style={{ position: "absolute", bottom: 4, right: 4, width: 14, height: 14, borderRadius: "50%", background: G, border: "2.5px solid #fff" }} />
              </div>

              {/* Infos */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 18, fontWeight: 700, color: "#111827", margin: "0 0 3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {form.name || "Mon compte"}
                </p>
                <p style={{ fontSize: 13, color: "#9ca3af", margin: "0 0 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {form.email}
                </p>
                {memberSince && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: G, background: G_LIGHT, padding: "3px 10px", borderRadius: 99 }}>
                    Membre depuis {memberSince}
                  </span>
                )}
              </div>
            </div>
          </motion.div>

          {/* ── Card formulaire ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16, duration: 0.4 }}
            style={{ background: "#fff", borderRadius: 20, border: "1px solid #efefef", padding: "28px", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>

            <p style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 22 }}>
              Informations personnelles
            </p>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>

              <Field
                icon={<User size={16} />}
                label="Nom complet"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Votre nom"
              />

              <Field
                icon={<Mail size={16} />}
                label="Adresse e-mail"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="votre@email.com"
              />

              <Field
                icon={<Phone size={16} />}
                label="Téléphone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+269 ..."
              />

              {/* Feedback */}
              <AnimatePresence mode="wait">
                {status === "success" && (
                  <motion.div key="success"
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    style={{ display: "flex", alignItems: "center", gap: 8, background: G_LIGHT, borderRadius: 10, padding: "10px 14px" }}>
                    <CheckCircle2 size={15} color={G} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: G }}>Profil mis à jour avec succès</span>
                  </motion.div>
                )}
                {status === "error" && (
                  <motion.div key="error"
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    style={{ display: "flex", alignItems: "center", gap: 8, background: "#fef2f2", borderRadius: 10, padding: "10px 14px" }}>
                    <AlertCircle size={15} color="#ef4444" />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#ef4444" }}>Une erreur est survenue. Réessayez.</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Bouton */}
              <button
                type="submit"
                disabled={loading || !dirty}
                style={{
                  width: "100%", padding: "14px 0",
                  background: loading || !dirty ? "#e5e7eb" : G,
                  color: loading || !dirty ? "#9ca3af" : "#fff",
                  border: "none", borderRadius: 12,
                  fontSize: 14, fontWeight: 700,
                  cursor: loading || !dirty ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  transition: "background 0.2s, color 0.2s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  WebkitTapHighlightColor: "transparent",
                }}
                onMouseEnter={e => { if (!loading && dirty) e.currentTarget.style.backgroundColor = G_DARK; }}
                onMouseLeave={e => { if (!loading && dirty) e.currentTarget.style.backgroundColor = G; }}>
                {loading
                  ? <>
                      <div style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                      Enregistrement...
                    </>
                  : dirty
                  ? "Enregistrer les modifications →"
                  : "Aucune modification"}
              </button>

            </form>
          </motion.div>

          {/* ── Séparateur + zone danger ── */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.28 }}
            style={{ marginTop: 16, background: "#fff", borderRadius: 20, border: "1px solid #fecaca", padding: "20px 28px", boxShadow: "0 2px 12px rgba(0,0,0,0.03)" }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#fca5a5", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
              Zone sensible
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: "#374151", margin: "0 0 2px" }}>Supprimer mon compte</p>
                <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>Cette action est irréversible et supprime toutes vos données.</p>
              </div>
              <button style={{
                fontSize: 12, fontWeight: 700, color: "#ef4444",
                background: "#fef2f2", border: "1.5px solid #fecaca",
                borderRadius: 10, padding: "8px 16px", cursor: "pointer",
                fontFamily: "inherit", transition: "all 0.18s", whiteSpace: "nowrap",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.borderColor = "#ef4444"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fef2f2"; e.currentTarget.style.borderColor = "#fecaca"; }}>
                Supprimer
              </button>
            </div>
          </motion.div>

        </div>
      </div>
    </>
  );
}